import type { MarketDataUnavailablePayload, MarketDataUnavailableReason } from "../types/marketDataUnavailable";
import type { MarketDataConnectOption } from "../types/marketDataAttribution";
import type { CandleTimeframePreviewResponse } from "../types/tradeManagementReport";
import type { ExecutionCandlesResponse } from "../types/trade";
import {
  executionCandleTypeRank,
  formatExecutionCandleTypeLabel,
} from "./executionCandleTimeframe";
import { isExecutionCandlesSynthesizedHistory } from "./executionCandlesStatus";
import {
  isBrokerConnectMarketDataOption,
  marketDataConnectGateFromApiError,
  type MarketDataConnectGateState,
} from "./marketDataConnectGate";
import { humanizeMarketDataBrokerSlug } from "./marketDataSourceChoice";
import {
  isMarketDataPreferenceUnavailableDetail,
  MarketDataUnresolvedError,
} from "./marketDataResolution";

const HISTORY_INSUFFICIENT_SUFFIX = "_history_insufficient";

const EXACT_MARKET_DATA_UNAVAILABLE_REASONS = new Set<string>([
  "no_trade_span",
  "market_data_preference_unavailable",
  "market_data_connector_required",
  "market_data_consent_required",
]);

function isKnownMarketDataUnavailableReason(value: string): value is MarketDataUnavailableReason {
  if (EXACT_MARKET_DATA_UNAVAILABLE_REASONS.has(value)) return true;
  return value.endsWith(HISTORY_INSUFFICIENT_SUFFIX);
}

function pickMarketDataUnavailableReason(body: Record<string, unknown>): string | undefined {
  const candidates = [
    body.chart_unavailable_reason,
    body.message,
    body.error,
    body.reason,
  ];
  for (const candidate of candidates) {
    const value = readString(candidate);
    if (value && isKnownMarketDataUnavailableReason(value)) {
      return value;
    }
  }
  return (
    readString(body.chart_unavailable_reason) ??
    readString(body.error) ??
    readString(body.reason)
  );
}

function resolveMarketDataUnavailableMessage(
  reason: MarketDataUnavailableReason,
  rawMessage?: string
): string {
  const message = rawMessage?.trim();
  if (message && message !== reason && !/^[a-z0-9_.]+$/.test(message)) {
    return message;
  }
  if (reason === "market_data_consent_required") {
    return "Grant consent for this symbol's OHLC source to fetch candles from your connected broker account.";
  }
  if (isMarketDataHistoryInsufficientReason(reason)) {
    return "This candle timeframe is not available for the selected date range on your OHLC source.";
  }
  return "Chart data is unavailable for this symbol and timeframe.";
}

function unwrapApiDetail(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  const o = value as Record<string, unknown>;
  const detail = o.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return detail as Record<string, unknown>;
  }
  return o;
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function readStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value
    .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
    .filter(Boolean);
  return items.length > 0 ? items : undefined;
}

function readConnectOptions(value: unknown): MarketDataConnectOption[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const options = value.filter(
    (entry): entry is MarketDataConnectOption =>
      Boolean(entry) && typeof entry === "object" && typeof (entry as { broker?: unknown }).broker === "string"
  );
  return options.length > 0 ? options : undefined;
}

export function marketDataUnavailableReasonFromBody(
  value: unknown
): MarketDataUnavailableReason | null {
  const body = unwrapApiDetail(value);
  if (!body) return null;
  const reason = pickMarketDataUnavailableReason(body);
  if (!reason) return null;

  if (isKnownMarketDataUnavailableReason(reason)) {
    return reason;
  }

  if (body.chart_available === false && reason) {
    return reason;
  }

  return null;
}

export function isMarketDataHistoryInsufficientReason(
  reason: string | null | undefined
): boolean {
  if (!reason) return false;
  return reason.endsWith(HISTORY_INSUFFICIENT_SUFFIX);
}

export function parseMarketDataUnavailablePayload(
  value: unknown
): MarketDataUnavailablePayload | null {
  const body = unwrapApiDetail(value);
  if (!body) return null;

  const reason = marketDataUnavailableReasonFromBody(body);
  if (!reason) return null;

  const message = resolveMarketDataUnavailableMessage(
    reason,
    readString(body.message)
  );

  const suggestedCandleTypes =
    readStringArray(body.suggested_candle_types) ??
    readStringArray(
      (body.history_limit as { suggested_candle_types?: unknown } | undefined)
        ?.suggested_candle_types
    );

  const earliestAvailable =
    readString(body.earliest_available) ??
    readString(
      (body.history_limit as { earliest_available?: unknown } | undefined)
        ?.earliest_available
    );

  const maxLookbackDays =
    typeof body.max_lookback_days === "number" && Number.isFinite(body.max_lookback_days)
      ? body.max_lookback_days
      : undefined;

  const maxCandles =
    typeof body.max_candles === "number" && Number.isFinite(body.max_candles)
      ? body.max_candles
      : undefined;

  const limitBasis = readString(body.limit_basis);

  const tradeAnalyticsAvailable =
    typeof body.trade_analytics_available === "boolean"
      ? body.trade_analytics_available
      : undefined;

  return {
    reason,
    message,
    suggestedCandleTypes,
    earliestAvailable,
    maxLookbackDays,
    maxCandles,
    limitBasis,
    mdSource: readString(body.md_source) ?? readString(body.market_data_broker),
    tradeAnalyticsAvailable,
    connectOptions: readConnectOptions(body.connect_options),
  };
}

export function isHyperliquidMarketDataSource(
  brokerOrSource?: string | null
): boolean {
  return (brokerOrSource ?? "").trim().toLowerCase() === "hyperliquid";
}

/** Sub-hourly = finer than 1h (1m–30m). */
export function isSubHourlyExecutionCandleType(
  timeframe?: string | null
): boolean {
  const rank = executionCandleTypeRank(timeframe);
  const hourRank = executionCandleTypeRank("h");
  if (rank < 0 || hourRank < 0) return false;
  return rank < hourRank;
}

export function cohortStartOlderThanDays(
  start: Date | null | undefined,
  days: number,
  now: Date = new Date()
): boolean {
  if (!start || !Number.isFinite(start.getTime())) {
    return true;
  }
  const cutoff = new Date(now);
  cutoff.setDate(cutoff.getDate() - days);
  return start.getTime() < cutoff.getTime();
}

/** Inline row chart has no TF picker — point users at Trade Review. */
export function buildInlineTradeReviewCoarserTimeframeHint(
  suggestedCandleTypes?: string[]
): string {
  const labels = (suggestedCandleTypes ?? [])
    .map((tf) => formatExecutionCandleTypeLabel(tf))
    .filter(Boolean)
    .slice(0, 3);
  if (labels.length === 0) {
    return "Open Trade Review for this symbol and choose a coarser candle timeframe.";
  }
  const examples =
    labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
  return `Open Trade Review for this symbol and try a coarser candle timeframe (e.g. ${examples}).`;
}

export function formatMarketDataEarliestAvailable(
  iso: string | undefined,
  locale?: string
): string | undefined {
  if (!iso?.trim()) return undefined;
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return iso;
  return date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function historyLimitConnectOptions(
  raw: ExecutionCandlesResponse["history_limit"]
): MarketDataConnectOption[] | undefined {
  if (!raw?.connect_options?.length) return undefined;
  return readConnectOptions(raw.connect_options);
}

/** Block generate when BE preflight sets can_generate false (ignore ohlc_status alone). */
export function marketDataUnavailableFromCandleTimeframePreview(
  preview: CandleTimeframePreviewResponse | null | undefined
): MarketDataUnavailablePayload | null {
  const readiness = preview?.market_data_readiness;
  if (!readiness || readiness.can_generate !== false) return null;

  return parseMarketDataUnavailablePayload({
    ...readiness,
    chart_available: false,
    error: readiness.chart_unavailable_reason,
    reason: readiness.chart_unavailable_reason,
    suggested_candle_types: readiness.suggested_candle_types,
    connect_options: readiness.connect_options,
  });
}

export function isBrokerAuthFailedMarketDataBody(value: unknown): boolean {
  const body = unwrapApiDetail(value);
  if (!body) return false;
  if (body.broker_auth_failed === true) return true;
  return body.status === "failed" && body.suggest_switch_source === true;
}

/** Map Liquid/BYOK MD auth rejection (strip poll or execution-candles echo) to switch-source UI. */
export function marketDataUnavailableFromBrokerAuthFailure(
  value: unknown
): MarketDataUnavailablePayload | null {
  const body = unwrapApiDetail(value);
  if (!body || !isBrokerAuthFailedMarketDataBody(body)) return null;

  const message =
    readString(body.error_message) ??
    readString(body.message) ??
    "Broker market data credentials were rejected. Try another data source.";

  const brokerPref = readString(body.broker_pref);
  const connectOptions =
    brokerPref && isBrokerConnectMarketDataOption(brokerPref)
      ? [
          {
            broker: brokerPref,
            label: `Connect ${humanizeMarketDataBrokerSlug(brokerPref) ?? brokerPref}`,
          },
        ]
      : undefined;

  return {
    reason: "market_data_preference_unavailable",
    message,
    tradeAnalyticsAvailable: true,
    mdSource: brokerPref,
    connectOptions,
  };
}

export function marketDataUnavailableFromExecutionCandles(
  data: ExecutionCandlesResponse | null | undefined
): MarketDataUnavailablePayload | null {
  if (!isExecutionCandlesSynthesizedHistory(data)) return null;
  const hl = data!.history_limit!;
  const reasonRaw = String(hl.reason ?? "hyperliquid_history_insufficient").trim();
  const reason: MarketDataUnavailableReason = isMarketDataHistoryInsufficientReason(reasonRaw)
    ? reasonRaw
    : "hyperliquid_history_insufficient";

  return {
    reason,
    message:
      hl.message?.trim() ??
      "This candle timeframe is not available natively for this date range on your OHLC source.",
    suggestedCandleTypes: hl.suggested_candle_types?.filter(Boolean),
    earliestAvailable: hl.earliest_available ?? hl.earliest_native_interval,
    maxLookbackDays: hl.max_lookback_days,
    maxCandles: hl.max_candles,
    limitBasis: hl.limit_basis,
    tradeAnalyticsAvailable: true,
    connectOptions: historyLimitConnectOptions(hl),
  };
}

export class MarketDataUnavailableError extends Error {
  status: number;
  payload: MarketDataUnavailablePayload;

  constructor(payload: MarketDataUnavailablePayload, status = 422) {
    super(payload.message);
    this.name = "MarketDataUnavailableError";
    this.status = status;
    this.payload = payload;
  }
}

export function throwIfMarketDataUnavailable(
  response: Response,
  raw: unknown
): void {
  if (response.status !== 422) return;
  const payload = parseMarketDataUnavailablePayload(raw);
  if (payload) {
    throw new MarketDataUnavailableError(payload, 422);
  }
}

export type CandleTimeframePreviewFetchErrorState = {
  unavailablePayload: MarketDataUnavailablePayload;
  connectGate: MarketDataConnectGateState | null;
  shouldOpenInstrumentSetup: boolean;
};

/** Map candle-timeframe-preview 422 into wizard gates + optional instrument-setup nudge. */
export function candleTimeframePreviewFetchErrorState(
  error: unknown
): CandleTimeframePreviewFetchErrorState | null {
  if (error instanceof MarketDataUnavailableError) {
    const { payload } = error;
    const isConnectNudge =
      payload.reason === "market_data_preference_unavailable" ||
      payload.reason === "market_data_connector_required";
    return {
      unavailablePayload: payload,
      connectGate: isConnectNudge
        ? {
            message: payload.message,
            connect_options: payload.connectOptions ?? [],
            trade_analytics_available: payload.tradeAnalyticsAvailable,
          }
        : null,
      shouldOpenInstrumentSetup:
        payload.reason === "market_data_preference_unavailable",
    };
  }

  if (error instanceof MarketDataUnresolvedError) {
    const connectGate = marketDataConnectGateFromApiError(error.body);
    if (!connectGate) return null;
    const unavailablePayload = parseMarketDataUnavailablePayload(error.body);
    if (!unavailablePayload) return null;
    return {
      unavailablePayload,
      connectGate,
      shouldOpenInstrumentSetup: isMarketDataPreferenceUnavailableDetail(error.body),
    };
  }

  return null;
}

function joinGateMessageParts(...parts: Array<string | undefined>): string {
  return parts.map((part) => part?.trim()).filter(Boolean).join(" ");
}

/** Map proactive / resolve connect gates into the in-chart unavailable overlay payload. */
export function marketDataUnavailableFromConnectGate(
  gate: MarketDataConnectGateState
): MarketDataUnavailablePayload {
  return {
    reason: "market_data_preference_unavailable",
    message:
      joinGateMessageParts(gate.message, gate.action) ||
      "Chart data is unavailable for this symbol and timeframe.",
    tradeAnalyticsAvailable: gate.trade_analytics_available,
    connectOptions: gate.connect_options,
  };
}

/** Map execution-candles connector-required into the in-chart unavailable overlay payload. */
export function marketDataUnavailableFromConnectorRequired(input: {
  message?: string;
  action?: string;
  connect_options?: MarketDataConnectOption[];
  trade_analytics_available?: boolean;
}): MarketDataUnavailablePayload {
  return {
    reason: "market_data_connector_required",
    message:
      joinGateMessageParts(input.message, input.action) ||
      "Charts for this instrument need a connected market data source.",
    tradeAnalyticsAvailable: input.trade_analytics_available,
    connectOptions: input.connect_options,
  };
}

/** Unified OHLC block payload for Trade Review chart overlays (priority: candles 422 → proactive scan → connector gates). */
export function resolveTradeReviewChartUnavailablePayload(input: {
  marketDataUnavailable?: MarketDataUnavailablePayload | null;
  proactiveConnectGate?: MarketDataConnectGateState | null;
  connectorRequired?: {
    message?: string;
    action?: string;
    connect_options?: MarketDataConnectOption[];
    trade_analytics_available?: boolean;
  } | null;
  resolveConnectGate?: MarketDataConnectGateState | null;
}): MarketDataUnavailablePayload | null {
  if (input.marketDataUnavailable) return input.marketDataUnavailable;
  if (input.proactiveConnectGate) {
    return marketDataUnavailableFromConnectGate(input.proactiveConnectGate);
  }
  if (input.connectorRequired) {
    return marketDataUnavailableFromConnectorRequired(input.connectorRequired);
  }
  if (input.resolveConnectGate) {
    return marketDataUnavailableFromConnectGate(input.resolveConnectGate);
  }
  return null;
}
